import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { DirectionProvider } from "@radix-ui/react-direction";
import { translations, normalizeLang, isRtlLanguage, DEFAULT_LANG } from "../utils/i18n";
import { getStoredLang, setStoredLang } from "../utils/auth";
import { useUser } from "./userContext";
import { toast } from "../utils/toast";

const LanguageContext = createContext(null);

// Drives the authenticated app shell's language (sidebar, home, settings,
// the editor, etc.) — separate from the pre-login auth/onboarding pages,
// which keep their own existing `/login/:lang`-style URL-param language
// (a deliberate, independent mechanism: those pages need to be shareable/
// linkable in a given language before anyone is signed in).
//
// The user's own `lang` field (set at onboarding, changeable from the
// profile menu's language switcher) is the source of truth once loaded;
// localStorage only bridges the gap before that first load so the shell
// doesn't flash in the wrong language, and is unauthenticated pages'
// language cache — is not used to persist the profile-driven default.
export function LanguageProvider({ children }) {
  const { user, onboard } = useUser();
  const [language, setLanguageState] = useState(() => normalizeLang(getStoredLang() || DEFAULT_LANG));

  // The user's saved preference wins once it's loaded — e.g. logging in on a
  // new device/browser where localStorage has nothing (or a stale value)
  // yet.
  useEffect(() => {
    if (!user?.lang) return;
    const resolved = normalizeLang(user.lang);
    setLanguageState((current) => (current === resolved ? current : resolved));
    setStoredLang(resolved);
  }, [user?.lang]);

  useEffect(() => {
    document.documentElement.lang = language;
    // The `:lang(ar)` rule in app.css (the same one the auth/onboarding
    // pages rely on) already resolves this from the `lang` attribute above,
    // but it's a low-specificity selector — anything with its own
    // font-family rule of equal-or-higher specificity elsewhere in the app
    // shell would silently win over it. Setting it directly here too
    // guarantees the app shell always matches auth/onboarding's Arabic font
    // regardless of what any individual component's CSS does.
    const rtl = isRtlLanguage(language);
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.style.fontFamily = rtl ? "var(--font-arabic)" : "var(--font-sans)";
  }, [language]);

  const setLanguage = useMemo(
    () => (lang) => {
      const resolved = normalizeLang(lang);
      setLanguageState(resolved);
      setStoredLang(resolved);
      // Best-effort: the UI has already switched (localStorage keeps it
      // sticky on this device even if this fails), this just carries the
      // choice to other devices/sessions. onboard() is safe to re-call —
      // full_name/persona/lang simply update in place.
      if (user) {
        onboard(user.full_name, user.persona, resolved).catch(() => {
          toast.error(translations[resolved].ui.languageSyncError);
        });
      }
    },
    [user, onboard]
  );

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {/* Without this, every Radix popper-based primitive (dropdown menu,
          popover, tooltip, alert dialog) defaults to assuming LTR no matter
          what `dir` is on the DOM, so `align="start"`/`"end"` stay pinned to
          their LTR meaning instead of flipping with the reading direction —
          this is what actually mirrors menu alignment/positioning under
          Arabic, not the document `dir` attribute set above on its own. */}
      <DirectionProvider dir={isRtlLanguage(language) ? "rtl" : "ltr"}>{children}</DirectionProvider>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
}
